import { useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Upload, FileText } from 'lucide-react'
import { JarvisCard } from '@/components/JarvisCard'
import { useToast } from '@/components/Toast'
import { apiClient, unwrap } from '@/lib/apiClient'
import { getAccessToken, HAS_AUTH } from '@/lib/auth'
import { useCurrentUser } from '@/lib/hooks'

interface GuideUrlResponse {
  url: string | null
  fileName: string | null
  expiresAt: string | null
}

const ADMIN_OID = import.meta.env.VITE_ADMIN_USER_OID || ''

export function Guide() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const fileRef = useRef<HTMLInputElement>(null)
  const me = useCurrentUser()
  const isAdmin = Boolean(ADMIN_OID) && me.data?.id === ADMIN_OID

  const guide = useQuery<GuideUrlResponse>({
    queryKey: ['guide', 'url'],
    queryFn: () => unwrap<GuideUrlResponse>(apiClient.get('/guides/url')),
    // SAS expires in 60 min; refresh just before
    refetchInterval: 55 * 60_000,
    staleTime: 50 * 60_000,
    enabled: HAS_AUTH,
  })

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      const headers: Record<string, string> = {}
      if (HAS_AUTH) {
        try {
          const token = await getAccessToken()
          headers.Authorization = `Bearer ${token}`
        } catch {
          // proceed without token; backend will 401
        }
      }
      const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || '/api'}/guides/upload`, {
        method: 'POST',
        headers,
        body: form,
      })
      const json = (await res.json()) as { success: boolean; data?: unknown; error?: string }
      if (!json.success) throw new Error(json.error ?? `Upload failed (${res.status})`)
      return json.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guide', 'url'] })
      toast.success('Guide uploaded')
    },
    onError: (e) => toast.error('Upload failed', (e as Error).message),
  })

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.type !== 'application/pdf') {
      toast.error('PDF required', `Got ${file.type || 'unknown type'}`)
      return
    }
    upload.mutate(file)
    e.target.value = ''
  }

  return (
    <div className="flex flex-col h-[calc(100vh-7rem)]">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            The Build Operator Method
          </h1>
          <p className="text-sm text-jarvis-muted mt-1">
            The playbook behind this dashboard — read it anytime.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => fileRef.current?.click()}
            disabled={upload.isPending}
            className="text-sm flex items-center gap-2 px-4 py-2 rounded-lg bg-jarvis-blue hover:bg-jarvis-blue-bright text-white transition-colors disabled:opacity-50"
          >
            <Upload size={14} />
            {upload.isPending
              ? 'Uploading…'
              : guide.data?.url
                ? 'Replace guide'
                : 'Upload guide (PDF)'}
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={handleFile}
        />
      </div>

      {guide.isLoading && (
        <JarvisCard>
          <div className="text-sm text-jarvis-muted py-12 text-center">Loading…</div>
        </JarvisCard>
      )}

      {!guide.isLoading && !guide.data?.url && (
        <JarvisCard className="flex-1 flex flex-col items-center justify-center py-16 text-center">
          <FileText size={48} className="text-jarvis-muted mb-4" />
          <p className="text-sm text-jarvis-muted max-w-md">
            {isAdmin
              ? 'No guide uploaded yet. Click "Upload guide" above to add your PDF.'
              : 'Guide coming soon — KC hasn\'t uploaded the playbook yet.'}
          </p>
        </JarvisCard>
      )}

      {guide.data?.url && (
        <iframe
          src={guide.data.url}
          title={guide.data.fileName ?? 'Guide'}
          className="flex-1 w-full rounded-lg border border-jarvis-border bg-jarvis-surface-2"
        />
      )}
    </div>
  )
}
